import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Fuel, BookOpen, ArrowRight, Ticket, Settings, CheckCircle, AlertTriangle, BarChart3, Users, FileText, Wallet, User as UserIcon, IndianRupee, Calendar, ScanEye, Landmark, Layers, ClipboardCheck, ClipboardList, Archive, LayoutDashboard, Menu, X, Star, ShieldCheck, Droplet, Bell, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import AppFooter from "@/components/AppFooter";
import LiveNozzleStatus from "@/components/LiveNozzleStatus";
import ManagerCashbookWidget from "@/components/ManagerCashbookWidget";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import LogoutButton from "@/components/LogoutButton";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import TrialBanner from "@/components/TrialBanner";
import DealerDailyBriefing from "@/components/DealerDailyBriefing";
import DealerHero from "@/pages/dealer/DealerHero";
import AiInsightsCard from "@/components/AiInsightsCard";
import ManagerBankDepositAlert from "@/components/ManagerBankDepositAlert";
import ManagerOpsQueue from "@/components/ManagerOpsQueue";
import ManagerMiniTrends from "@/components/ManagerMiniTrends";
import ManagerPendingHandoversCard from "@/components/ManagerPendingHandoversCard";
import TasksSummaryCard from "@/components/TasksSummaryCard";
import { MASKED_ROUTES } from "@/lib/routeMask";
import { useEnv } from "@/components/EnvBanner";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, FlaskConical, Package, Thermometer, Calculator, FolderLock, LifeBuoy, Truck, Receipt, Terminal, ChevronDown, ChevronRight } from "lucide-react";
import AttendanceSummaryCard from "@/components/AttendanceSummaryCard";
import SidebarReferralCard from "@/components/SidebarReferralCard";
import { useTranslation } from "react-i18next";
import {
  MonthlyPnlBar, WaterfallCard, HisabFunnelCard, HourlyHeatmapCard,
  TopNozzlesCard, StaffPerformanceCard,
} from "@/components/HomeVisuals";

export default function Home() {
  const { t: __t } = useTranslation();
  const { user, pump } = useAuth();
  const nav = useNavigate();

  // Phase 2: CSAs go straight to their focused portal — no dealer/manager chrome.
  // Also short-circuits any accidental navigation to `/` after login.
  useEffect(() => {
    if (user?.role === "CSA") nav("/csa", { replace: true });
  }, [user?.role, nav]);

  // Both DEALER and MANAGER get the sidebar-based shell. Only CSA + fallback
  // roles continue with the old max-w-6xl card grid.
  if (user?.role === "DEALER" || user?.role === "MANAGER") {
    return <RoleShell user={user} pump={pump} role={user.role} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary font-black text-primary-foreground shadow-sm" data-testid="brand-logo">
              P
            </div>
            <div>
              <div className="font-display text-lg font-bold tracking-tight" data-testid="brand-title">{__t("home.hisabx")}</div>
              <div className="text-xs text-muted-foreground">{pump?.name || "—"}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="hidden text-xs text-muted-foreground md:block">
              {user?.name} · <span className="font-semibold text-foreground" data-testid="user-role">{user?.role}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => nav("/profile")} data-testid="profile-btn" title={__t("csa.csaportal.profile_password")}>
              <UserIcon className="h-4 w-4" />
            </Button>
            <LogoutButton where="home" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12">
        <section className="mb-10 enter-fade">
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-5xl">
            Welcome back, <span className="text-primary">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Manage your daily HISAB, track cash flow, and keep customers in the loop.
          </p>
        </section>

        <section className="stagger grid gap-4 md:grid-cols-3">
          {/* Live status is visible to Manager only (dealers get it inside DealerHero if needed) */}
          {user?.role === "MANAGER" && (
            <div className="md:col-span-3"><LiveNozzleStatus /></div>
          )}

          {/* Iter 74 · Manager Cashbook widget — cash-in-hand + quick deposit */}
          {user?.role === "MANAGER" && (
            <div className="md:col-span-3"><ManagerCashbookWidget /></div>
          )}

          {/* Manager summary strip — shows pending HISABs, active shifts, month shortage */}
          {user?.role === "MANAGER" && (
            <div className="md:col-span-3"><ManagerSummaryStrip /></div>
          )}

          {/* Pump Registers module deprecated (Feb 2026): Stock+Density folded
              into Tank Management; Lube folded into Lube Inventory. */}

          {/* Reading Verification Hub was removed — CSAs record readings
              in shift-close, verify happens in Manager → Verify HISABs. */}

          {/* CSA sees only their shift tile */}
          {user?.role === "CSA" && (
            <FeatureCard
              icon={<Fuel className="h-5 w-5 text-primary" />}
              title={__t("home.my_shift")}
              desc="Check-in, allocate nozzles, record entries, close shift."
              to="/csa"
              testId="link-csa"
              featured
            />
          )}

          {/* Manager: day-to-day operational tiles */}
          {user?.role === "MANAGER" && (
            <>
              <FeatureCard
                icon={<Users className="h-5 w-5 text-primary" />}
                title="Attendance · Today"
                desc="Mark salesmen Present / Late / Absent. Manager's mark wins."
                to="/manager/attendance"
                testId="link-attendance"
                featured
              />
              <FeatureCard
                icon={<Users className="h-5 w-5 text-primary" />}
                title="Payroll · Hours"
                desc="Range-based hours rollup + adjust mis-recorded shifts."
                to="/manager/payroll"
                testId="link-payroll"
              />
              <FeatureCard
                icon={<CheckCircle className="h-5 w-5 text-primary" />}
                title="Hisab Center"
                desc="Verify, review & close every shift. Cycle summary + hex tiles for each CSA HISAB."
                to="/manager/day-close"
                testId="link-hisab-center"
                featured
              />
              <FeatureCard
                icon={<AlertTriangle className="h-5 w-5 text-primary" />}
                title={__t("home.shift_overrides")}
                desc="Unlock a CSA's frozen nozzles · extend submit time · end a stuck shift."
                to="/manager/shift-overrides"
                testId="link-shift-overrides"
              />
              <FeatureCard
                icon={<ScanEye className="h-5 w-5 text-primary" />}
                title={__t("home.fleet_card_scans")}
                desc="Scan Sodexo / Buddy4Cash / BPCL SmartFleet slips into online transactions."
                to="/dealer/online-payments?tab=fleet"
                testId="link-fleet-card"
              />
              <FeatureCard
                icon={<BookOpen className="h-5 w-5 text-primary" />}
                title={__t("home.daily_cashbook")}
                desc="Cash in/out, deposits & closing balance."
                to="/manager/cashbook"
                testId="link-cashbook"
              />
              <FeatureCard
                icon={<Landmark className="h-5 w-5 text-primary" />}
                title={__t("home.bank_deposits")}
                desc="Deposit CSA cash to the bank. Live cash-in-hand tracked."
                to="/manager/bank-deposits"
                testId="link-bank-deposits"
              />
              <FeatureCard
                icon={<ScanEye className="h-5 w-5 text-primary" />}
                title="Nozzle Verify · Live"
                desc="Independently read nozzles. Mismatches vs CSA are flagged."
                to="/manager/nozzle-verify"
                testId="link-nozzle-verify"
              />
              <FeatureCard
                icon={<BookOpen className="h-5 w-5 text-primary" />}
                title={__t("home.ledgers")}
                desc="Customer deposits, running cash & staff advances."
                to="/manager/ledgers"
                testId="link-ledgers"
              />
              <FeatureCard
                icon={<AlertTriangle className="h-5 w-5 text-primary" />}
                title={__t("home.shortage_ledger")}
                desc="Track monthly shortages by staff."
                to="/manager/shortage"
                testId="link-shortage"
              />
              <FeatureCard
                icon={<Fuel className="h-5 w-5 text-primary" />}
                title={__t("home.nozzle_continuity")}
                desc="Every nozzle's opening ↔ closing meter across shifts. Mismatches highlighted."
                to="/nozzles/continuity"
                testId="link-nozzle-continuity"
              />
              <FeatureCard
                icon={<Calendar className="h-5 w-5 text-primary" />}
                title={__t("home.leave_requests")}
                desc="Approve/reject CSA leave requests."
                to="/leave-requests"
                testId="link-leave-requests-mgr"
              />
            </>
          )}

          {/* CSA-only: personal leave request */}
          {user?.role === "CSA" && (
            <FeatureCard
              icon={<Calendar className="h-5 w-5 text-primary" />}
              title={__t("home.request_leave")}
              desc="Submit a leave request to your manager."
              to="/leave-requests"
              testId="link-leave-csa"
            />
          )}
        </section>
        <AppFooter />
      </main>
    </div>
  );
}

/**
 * Role-based sidebar shell used by both DEALER and MANAGER.
 * Rendering rules:
 *   - Dealer sees every internal tool grouped Overview/Daily Ops/Reports/Setup/Admin.
 *   - Manager sees a strict subset — no Setup/Admin/Dealer-only tools.
 *   - Pinned items live in a "Pinned" section at the top of the sidebar and
 *     persist per-device via localStorage (key = `hisabx.pins.{role}`).
 */
function RoleShell({ user, pump, role }) {
  const { t: __t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [pins, setPins] = useState(() => loadPins(role));
  // Desktop-only sidebar collapse. Persisted per-device.
  const SIDEBAR_KEY = "hisabx.sidebar.collapsed";
  const [desktopNavCollapsed, setDesktopNavCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
  });
  const toggleDesktopNav = () => {
    setDesktopNavCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // Close mobile drawer on route change
  useEffect(() => { setMobileNavOpen(false); }, [loc.pathname]);

  // Lock body scroll while mobile drawer is open — otherwise the page
  // behind scrolls instead of the drawer's own content.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileNavOpen]);

  const isDealer = role === "DEALER";
  const nav_sections = useMemo(() => {
    // ---- Manager view (lean, verify-first) ----
    if (!isDealer) {
      return [
        {
          label: "Overview",
          items: [
            { icon: LayoutDashboard, title: "Dashboard", to: "/", testId: "nav-dashboard", featured: true },
          ],
        },
        {
          label: "Daily Ops",
          items: [
            { icon: CheckCircle, title: "Hisab Center", to: "/manager/day-close", testId: "nav-hisab-center", featured: true },
            { icon: ClipboardList, title: "Tasks", to: "/tasks", testId: "nav-tasks" },
            { icon: BookOpen, title: "Stock Register", to: "/dealer/stock-register", testId: "nav-stock-register" },
            { icon: Thermometer, title: "Temp & Density", to: "/dealer/density-register", testId: "nav-density-register" },
            { icon: Calculator, title: "Density Calculator", to: "/tools/density", testId: "nav-density-calc" },
            { icon: FolderLock, title: "Documents Vault", to: "/dealer/documents", testId: "nav-documents-vault" },
        { icon: Truck, title: "Tank Truck Log", to: "/dealer/tt", testId: "nav-tt-log" },
        { icon: Receipt, title: "Credit Invoices", to: "/dealer/invoices", testId: "nav-invoices" },
            { icon: Fuel, title: "Tank Stock", to: "/dealer/tank-management", testId: "nav-tank-management" },
            { icon: BookOpen, title: "Cashbook", to: "/manager/cashbook", testId: "nav-cashbook" },
            { icon: Landmark, title: "Bank Deposits", to: "/manager/bank-deposits", testId: "nav-bank-deposits" },
            { icon: AlertTriangle, title: "Shift Management", to: "/manager/shift-overrides", testId: "nav-shift-overrides" },
          ],
        },
        {
          label: "Reports",
          items: [
            { icon: Fuel, title: "Nozzle Continuity", to: "/nozzles/continuity", testId: "nav-nozzle-continuity" },
            { icon: AlertTriangle, title: "Shortage Ledger", to: "/manager/shortage", testId: "nav-shortage" },
            { icon: ScanEye, title: "Online Payments", to: "/dealer/online-payments", testId: "nav-online-payments" },
            { icon: BarChart3, title: "Expense Analytics", to: "/dealer/expense-analytics", testId: "nav-expense-analytics" },
          ],
        },
        {
          label: "Team",
          items: [
            { icon: ShieldCheck, title: "My Attendance", to: "/manager/my-attendance", testId: "nav-my-attendance", featured: true },
            { icon: Calendar, title: "Leave Requests", to: "/leave-requests", testId: "nav-leave-requests" },
            { icon: Users, title: "Manager Attendance", to: "/manager/attendance", testId: "nav-attendance" },
            { icon: Users, title: "Credit Customers", to: "/dealer/credit-customers", testId: "nav-credit-customers-mgr" },
            { icon: LifeBuoy, title: "Help & Support", to: "/help", testId: "nav-help-support-mgr" },
          ],
        },
      ];
    }
    // ---- Dealer view (all sections) ----
    return [
    {
      label: "Overview",
      items: [
        { icon: LayoutDashboard, title: "Dashboard", to: "/", masked: "/x/dh", testId: "nav-dashboard", featured: true },
      ],
    },
    {
      label: "Daily Ops",
      items: [
        { icon: CheckCircle, title: "Hisab Center", to: "/manager/day-close", testId: "nav-hisab-center", featured: true },
        { icon: ClipboardList, title: "Tasks", to: "/tasks", testId: "nav-tasks" },
        { icon: BookOpen, title: "Stock Register", to: "/dealer/stock-register", testId: "nav-stock-register" },
        { icon: Thermometer, title: "Temp & Density", to: "/dealer/density-register", testId: "nav-density-register" },
        { icon: Calculator, title: "Density Calculator", to: "/tools/density", testId: "nav-density-calc" },
        { icon: FolderLock, title: "Documents Vault", to: "/dealer/documents", testId: "nav-documents-vault" },
        { icon: Truck, title: "Tank Truck Log", to: "/dealer/tt", testId: "nav-tt-log" },
        { icon: Receipt, title: "Credit Invoices", to: "/dealer/invoices", testId: "nav-invoices" },
        { icon: Fuel, title: "Tank Stock", to: "/dealer/tank-management", testId: "nav-tank-management" },
        { icon: BookOpen, title: "Cashbook", to: "/manager/cashbook", testId: "nav-cashbook" },
        { icon: Landmark, title: "Bank Deposits", to: "/manager/bank-deposits", testId: "nav-bank-deposits" },
        { icon: AlertTriangle, title: "Shift Management", to: "/manager/shift-overrides", testId: "nav-shift-overrides" },
        { icon: Calendar, title: "Leave Requests", to: "/leave-requests", testId: "nav-leave-requests" },
      ],
    },
    {
      label: "Reports",
      items: [
        { icon: Fuel, title: "Nozzle Continuity", to: "/nozzles/continuity", testId: "nav-nozzle-continuity" },
        { icon: AlertTriangle, title: "Shortage Ledger", to: "/manager/shortage", testId: "nav-shortage" },
        { icon: ScanEye, title: "Online Payments", to: "/dealer/online-payments", testId: "nav-online-payments" },
        { icon: BarChart3, title: "Expense Analytics", to: "/dealer/expense-analytics", testId: "nav-expense-analytics" },
      ],
    },
    {
      label: "Setup",
      items: [
        { icon: Settings, title: "Pump Setup", to: "/setup", testId: "nav-setup" },
        { icon: Fuel, title: "Dispenser Setup", to: "/dealer/dispensers", testId: "nav-dispensers" },
        { icon: Droplet, title: "Tanks & Alerts", to: "/dealer/tanks", testId: "nav-tanks" },
        { icon: Package, title: "Lube Inventory", to: "/dealer/lube", testId: "nav-lube-inventory" },
        { icon: IndianRupee, title: "Product Rates", to: "/dealer/rates", testId: "nav-rates" },
        { icon: Users, title: "Credit Customers", to: "/dealer/credit-customers", testId: "nav-credit-customers" },
        { icon: Ticket, title: "Staff Invites", to: "/dealer/invites", testId: "nav-invites" },
        { icon: Calendar, title: "CA Add-on", to: "/dealer/ca-addon", testId: "nav-ca-addon" },
      ],
    },
    {
      label: "Alerts",
      items: [
        { icon: Bell, title: "Notification Channels", to: "/dealer/alerts", testId: "nav-alerts-hub", featured: true },
        { icon: LifeBuoy, title: "Help & Support", to: "/help", testId: "nav-help-support" },
      ],
    },
    {
      label: "Admin",
      // Discreet by default — collapsed on first render, expand only when the
      // dealer clicks the section header. Housekeeping tools live here.
      collapsedByDefault: true,
      items: [
        { icon: Archive, title: "Data · Backup & Reset", to: "/dealer/data-ops", testId: "nav-data-ops" },
        { icon: Terminal, title: "Ops Console", to: "/dealer/ops-console", testId: "nav-ops-console" },
      ],
    },
  ];
  }, [isDealer]);

  // Flatten all items → look up by testId for the pinned section (avoids duplicating item definitions).
  const itemByTestId = useMemo(() => {
    const m = {};
    for (const sec of nav_sections) for (const it of sec.items) m[it.testId] = it;
    return m;
  }, [nav_sections]);
  const pinnedItems = pins.map((tid) => itemByTestId[tid]).filter(Boolean);

  const togglePin = (testId) => {
    setPins((prev) => {
      const next = prev.includes(testId) ? prev.filter((t) => t !== testId) : [...prev, testId];
      savePins(role, next);
      return next;
    });
  };

  const isActive = (item) => {
    // After URL masking every internal route resolves to a /x/{code} path.
    // Match against both the masked equivalent AND the raw path so managers
    // (whose sidebar items don't set `masked`) also light up correctly.
    const masked = MASKED_ROUTES[item.to] || item.to;
    if (item.testId === "nav-dashboard") return loc.pathname === "/x/dh" || loc.pathname === "/";
    return loc.pathname === masked || loc.pathname === item.to;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ============ Top bar (spans full width) ============ */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-3 px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground lg:hidden"
              onClick={() => setMobileNavOpen(true)}
              aria-label={__t("home.open_menu")}
              data-testid="dealer-nav-open"
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* Desktop sidebar collapse toggle */}
            <button
              type="button"
              className="hidden h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:bg-secondary lg:inline-flex"
              onClick={toggleDesktopNav}
              aria-label={desktopNavCollapsed ? "Show sidebar" : "Hide sidebar"}
              title={desktopNavCollapsed ? "Show sidebar" : "Hide sidebar"}
              data-testid="dealer-nav-toggle-desktop"
              aria-pressed={!desktopNavCollapsed}
            >
              {desktopNavCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary font-black text-primary-foreground shadow-sm" data-testid="brand-logo">
              P
            </div>
            <div className="min-w-0">
              <div className="font-display text-lg font-bold tracking-tight" data-testid="brand-title">{__t("home.hisabx")}</div>
              <div className="truncate text-xs text-muted-foreground">{pump?.name || "—"}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="hidden text-xs text-muted-foreground md:block">
              {user?.name} · <span className="font-semibold text-foreground" data-testid="user-role">{user?.role}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => nav("/profile")} data-testid="profile-btn" title={__t("csa.csaportal.profile_password")}>
              <UserIcon className="h-4 w-4" />
            </Button>
            <LogoutButton where="home" />
          </div>
        </div>
      </header>

      {/* ============ Sidebar + Main content ============ */}
      <div className="mx-auto flex max-w-[1440px]">
        {/* Sidebar — sticky on desktop, drawer on mobile. Desktop collapse
            simply hides the aside so <main> reflows to full width. */}
        <aside
          className={
            "fixed inset-y-0 left-0 z-50 w-72 transform overflow-y-auto overscroll-contain border-r border-border bg-card px-3 py-4 transition-transform duration-200 " +
            "lg:sticky lg:top-[57px] lg:z-0 lg:h-[calc(100vh-57px)] lg:translate-x-0 " +
            (mobileNavOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full") + " " +
            (desktopNavCollapsed ? "lg:hidden" : "lg:block")
          }
          data-testid="dealer-sidebar"
          aria-label="Dealer navigation"
          aria-hidden={desktopNavCollapsed || (!mobileNavOpen && typeof window !== "undefined" && window.innerWidth < 1024)}
        >
          {/* Mobile close button */}
          <div className="mb-3 flex items-center justify-between lg:hidden">
            <div className="text-sm font-bold">{__t("home.navigate")}</div>
            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              className="rounded-lg border border-border bg-background p-1.5"
              aria-label={__t("home.close_menu")}
              data-testid="dealer-nav-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <nav className="space-y-4">
            {pinnedItems.length > 0 && (
              <div data-testid="nav-section-pinned">
                <div className="mb-1.5 flex items-center gap-1.5 px-2 text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-400">
                  <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                  Pinned
                </div>
                <ul className="space-y-0.5">
                  {pinnedItems.map((item) => (
                    <SidebarRow key={"pin-" + item.testId} item={item} active={isActive(item)} isPinned onTogglePin={togglePin} />
                  ))}
                </ul>
              </div>
            )}
            {nav_sections.map((section) => (
              <SidebarSection
                key={section.label}
                section={section}
                pins={pins}
                onTogglePin={togglePin}
                isActive={isActive}
              />
            ))}
            {/* Brick 6 N16 — Referral CTA lives at the bottom of the dealer sidebar */}
            {isDealer && <SidebarReferralCard collapsed={false} />}
            <DevResetSection />
          </nav>
        </aside>

        {/* Overlay when mobile drawer open */}
        {mobileNavOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden
          />
        )}

        {/* Main content */}
        <main className="min-w-0 flex-1 px-4 py-6 md:px-6 lg:py-8">
          <section className="mb-4 enter-fade" data-testid="home-welcome">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {isDealer ? "Dealer console" : "Manager console"} · {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
            </div>
            <h1 className="mt-0.5 font-display text-lg font-bold tracking-tight md:text-xl">
              Hi <span className="text-primary">{user?.name?.split(" ")[0]}</span>
              <span className="ml-1 text-muted-foreground">— {isDealer ? "your pump at a glance." : "today's ops queue."}</span>
            </h1>
          </section>

          <div className="space-y-4">
            {/* Brick 5 N3 — Trial state renders as a compact chip when non-urgent (>30d)
                and expands into a full banner only when actionable (amber/red). */}
            {isDealer && pump && <TrialBanner pump={pump} onUpgrade={() => nav("/upgrade")} compact />}
            {!isDealer && <ManagerBankDepositAlert />}
            {!isDealer && <ManagerPendingHandoversCard />}
            {isDealer ? (
              <>
                {/* N4 — Live board full-width at the top */}
                <div data-testid="dealer-liveboard-full">
                  <LiveNozzleStatus />
                </div>
                <DealerDailyBriefing />
                <MonthlyPnlBar />
                <DealerHero pump={pump} />
                <div className="grid gap-4 lg:grid-cols-2" data-testid="home-waterfall-funnel-grid">
                  <WaterfallCard period="30d" />
                  <HisabFunnelCard period="30d" />
                </div>
                <HourlyHeatmapCard days={30} />
                <TasksSummaryCard />
                <DealerSecondaryCards />
              </>
            ) : (
              <>
                <LiveNozzleStatus />
                <ManagerSummaryStrip />
                {/* Testing Notes 12 · #5 — ops queue replaces AI Insights on the manager Home */}
                <ManagerOpsQueue />
                <TasksSummaryCard />
                <ManagerMiniTrends />
              </>
            )}
            {/* AI Insights is dealer-only. Managers now see the ops queue instead
                (Testing Notes 12 · #5 — "manager doesn't need AI insights"). */}
            {isDealer && (
              <div
                className="rounded-2xl border-2 border-purple-300/50 bg-gradient-to-br from-purple-500/[0.06] via-card to-fuchsia-500/[0.04] p-1 shadow-sm dark:border-purple-900/60"
                data-testid="ai-insights-panel"
              >
                <AiInsightsCard />
              </div>
            )}
            {isDealer && <AttendanceSummaryCard />}
            {isDealer && <OnboardingChecklist />}
          </div>

          <AppFooter />
        </main>
      </div>
    </div>
  );
}


// ---------- Sidebar row + pin helpers (used by both DEALER and MANAGER shells) ----------
function SidebarSection({ section, pins, onTogglePin, isActive }) {
  const [collapsed, setCollapsed] = useState(!!section.collapsedByDefault);
  const isCollapsible = !!section.collapsedByDefault;
  const label = section.label;
  return (
    <div data-testid={`nav-section-${label.toLowerCase()}`}>
      {isCollapsible ? (
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="mb-1.5 flex w-full items-center gap-1 rounded px-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground/70 transition-colors hover:text-foreground"
          data-testid={`nav-section-toggle-${label.toLowerCase()}`}
          aria-expanded={!collapsed}
        >
          {collapsed
            ? <ChevronRight className="h-3 w-3" />
            : <ChevronDown className="h-3 w-3" />}
          <span>{label}</span>
        </button>
      ) : (
        <div className="mb-1.5 px-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
          {label}
        </div>
      )}
      {!collapsed && (
        <ul className="space-y-0.5">
          {section.items.map((item) => (
            <SidebarRow key={item.testId} item={item} active={isActive(item)} isPinned={pins.includes(item.testId)} onTogglePin={onTogglePin} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SidebarRow({ item, active, isPinned, onTogglePin }) {
  const Icon = item.icon;
  return (
    <li className="group/row relative">
      <Link
        to={item.to}
        data-testid={item.testId}
        className={
          "flex items-center gap-2.5 rounded-lg px-2.5 py-2 pr-8 text-sm font-medium transition-colors " +
          (active
            ? "bg-primary/10 text-primary"
            : "text-foreground/80 hover:bg-secondary hover:text-foreground")
        }
      >
        <Icon className={"h-4 w-4 shrink-0 " + (active ? "text-primary" : "text-muted-foreground group-hover/row:text-foreground")} />
        <span className="truncate">{item.title}</span>
        {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />}
      </Link>
      {/* Pin toggle — always visible when pinned, appears on hover otherwise */}
      <button
        type="button"
        aria-label={isPinned ? `Unpin ${item.title}` : `Pin ${item.title}`}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onTogglePin(item.testId); }}
        className={
          "absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 transition-opacity " +
          (isPinned
            ? "text-amber-500 opacity-100"
            : "text-muted-foreground opacity-0 hover:text-foreground group-hover/row:opacity-70")
        }
        data-testid={`pin-toggle-${item.testId}`}
      >
        <Star className={"h-3.5 w-3.5 " + (isPinned ? "fill-amber-500" : "")} />
      </button>
    </li>
  );
}

const PIN_STORAGE_PREFIX = "hisabx.pins.";
function loadPins(role) {
  try {
    const raw = localStorage.getItem(PIN_STORAGE_PREFIX + role);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function savePins(role, list) {
  try { localStorage.setItem(PIN_STORAGE_PREFIX + role, JSON.stringify(list)); } catch { /* ignore */ }
}


/**
 * Dealer secondary cards — fetches /analytics/dashboard once and lays out the
 * three "leaderboard" style cards (Top nozzles, Top credit customers, Staff).
 * Extracted so Home.jsx stays readable and these can be tested independently.
 */
function DealerSecondaryCards() {
  const { t: __t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    let dead = false;
    import("@/lib/api").then(({ api }) => {
      api.get("/analytics/dashboard?period=30d")
        .then((r) => !dead && setData(r.data))
        .catch(() => !dead && setData({ top_nozzles: [], top_credit_customers: [], staff: [] }));
    });
    return () => { dead = true; };
  }, []);
  const nozzles = data?.top_nozzles || [];
  const staff = data?.staff || [];
  const credit = data?.top_credit_customers || [];
  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="home-secondary-cards">
      <TopNozzlesCard nozzles={nozzles} />
      {/* Top credit customers — inline, matches the pattern of the deleted Dashboard */}
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid="home-top-credit-card">
        <div className="flex items-center gap-2 text-base font-semibold">
          <Users className="h-4 w-4 text-primary" /> Top credit customers
        </div>
        <div className="mt-2">
          {credit.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">{__t("manager.no_outstanding_credit")}</div>
          ) : (
            credit.map((c) => (
              <div key={c.phone} className="flex items-center justify-between border-b py-2 text-sm last:border-0" data-testid={`home-credit-${c.phone}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.phone} · {c.vehicle || "-"}</div>
                </div>
                <div className="font-mono font-bold text-red-600">
                  ₹{Number(c.outstanding || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <StaffPerformanceCard staff={staff} />
    </div>
  );
}



function ManagerSummaryStrip() {
  const [s, setS] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      import("@/lib/api").then(({ api }) => api.get("/manager/dashboard/summary").then((r) => !cancelled && setS(r.data)).catch(() => {}));
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (!s) return null;
  const tiles = [
    { k: "pending_hisabs", label: "Pending to verify", tone: s.pending_hisabs > 0 ? "warn" : "ok", suffix: "" },
    { k: "approved_today", label: "Approved today", tone: "ok", suffix: "" },
    { k: "active_shifts", label: "Active shifts now", tone: "ok", suffix: "" },
    { k: "open_month_shortage", label: "This month shortage", tone: s.open_month_shortage > 0 ? "bad" : "ok", suffix: "Rs. " },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="manager-summary-strip">
      {tiles.map((t) => (
        <div key={t.k} className={"relative overflow-hidden rounded-xl border p-4 backdrop-blur-sm transition-all hover:-translate-y-0.5 " + (
          t.tone === "warn" ? "border-amber-500/30 bg-amber-500/5" :
          t.tone === "bad" ? "border-destructive/30 bg-destructive/5" :
          "border-border bg-card"
        )}>
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{t.label}</div>
          <div className={"mt-1 font-mono text-2xl font-semibold tabular-nums " + (
            t.tone === "warn" ? "text-amber-600 dark:text-amber-400" :
            t.tone === "bad" ? "text-destructive" :
            "text-foreground"
          )}>
            {t.suffix}{Number(s[t.k] || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </div>
        </div>
      ))}
    </div>
  );
}

function FeatureCard({ icon, title, desc, to, testId, featured }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={
        "group relative block overflow-hidden rounded-xl border p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_-10px_hsl(var(--primary)/0.25)] " +
        (featured
          ? "border-primary/40 bg-gradient-to-br from-primary/10 to-primary/[0.02] hover:border-primary/60"
          : "border-border bg-card hover:border-primary/40")
      }
    >
      {/* Ambient glow (top-right) */}
      <span className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-primary/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
      <div className="relative flex items-center gap-3">
        <div className={"flex h-10 w-10 items-center justify-center rounded-lg transition-transform group-hover:scale-110 " + (featured ? "bg-background shadow-sm ring-1 ring-primary/20" : "bg-secondary")}>
          {icon}
        </div>
        <div className="font-display text-base font-semibold text-foreground">{title}</div>
        <ArrowRight className="ml-auto h-4 w-4 -translate-x-1 text-muted-foreground opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100 group-hover:text-primary" />
      </div>
      <p className="relative mt-3 text-sm leading-relaxed text-muted-foreground">{desc}</p>
    </Link>
  );
}


/**
 * Preview-only dev tools — renders nothing on production.
 * The main affordance is a "Reset demo pump" button that re-seeds the whole
 * demo dataset in one click so testing feels like a game with a level select.
 */
function DevResetSection() {
  const { t: __t } = useTranslation();
  const env = useEnv();
  const [busy, setBusy] = useState(false);
  if (!env?.is_preview) return null;
  const reset = async () => {
    if (!window.confirm(
      "Reset the demo pump?\n\n" +
      "This wipes and re-seeds all demo data (attendance, HISABs, allocations, tanks…) " +
      "for the demo pump only. Your own production data on hisabx.in is NOT touched."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/dev/reset-demo-pump");
      toast.success(__t("home.demo_pump_reset_reloading"));
      // Give the user a beat to read the toast, then hard-reload so every
      // cached view picks up the fresh dataset.
      setTimeout(() => window.location.reload(), 900);
      console.log("[dev/reset]", data?.log_tail);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 rounded-xl border-2 border-dashed border-amber-500/60 bg-amber-50/60 p-3 dark:bg-amber-950/20" data-testid="dev-reset-section">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-800 dark:text-amber-300">
        <FlaskConical className="h-3 w-3" /> Dev tools
      </div>
      <button
        type="button"
        onClick={reset}
        disabled={busy}
        className="flex w-full items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-amber-950 shadow-sm transition-all hover:bg-amber-400 disabled:opacity-60"
        data-testid="dev-reset-demo-pump"
      >
        <RefreshCw className={"h-3.5 w-3.5 " + (busy ? "animate-spin" : "")} />
        {busy ? "Resetting…" : "Reset demo pump"}
      </button>
      <p className="mt-1.5 text-[10px] leading-tight text-amber-900/70 dark:text-amber-200/70">
        Wipes &amp; reseeds 7 days of history + 1 rejected HISAB + 1 combined-shift day. Preview only.
      </p>
    </div>
  );
}
